import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, ShoppingCart, Package, Factory, ClipboardList, Tag, FileCheck,
  Users, Shield, Key, LogOut, Menu, X, UserCircle, Building2, FileText,
  ShoppingBag, Truck, CheckCircle, FileX, DollarSign, Settings2, Clock,
  Activity, Cpu, Layers, Timer, AlertTriangle, Ban, BarChart3, Eye, Settings, Wrench,
  GitBranch, FileSpreadsheet, ListTodo, Ruler,
  Beaker, Droplet, Image, Scale, Thermometer, ClipboardCheck, FlaskConical, BookOpen,
  Boxes, FolderKanban, NotebookPen, PanelLeftClose, PanelLeftOpen, Search
} from 'lucide-react';

/*
 * Two-panel sidebar:
 *   ─ Narrow icon rail on the far left (module groups)
 *   ─ Secondary panel showing only the items of the active group
 *   ─ Active group is inferred from the current URL
 */

// Groups (each renders an icon in the rail and a panel of items when active).
const GROUPS = [
  {
    key: 'sales',
    label: 'Sales',
    icon: ShoppingCart,
    items: [
      { path: '/dashboard',     icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/quotations',    icon: FileText,        label: 'Quotations' },
      { path: '/sales-orders',  icon: ShoppingCart,    label: 'Sales Orders' },
      { path: '/packing',       icon: ClipboardList,   label: 'Packing Lists' },
      { path: '/labels',        icon: Tag,             label: 'Labels' },
      { path: '/coa',           icon: FileCheck,       label: 'Certificate of Analysis' },
      { path: '/stock-report',  icon: BarChart3,       label: 'Stock Report' },
      { path: '/inventory',     icon: Package,         label: 'SKU / Inventory' },
    ],
  },
  {
    key: 'purchase',
    label: 'Purchase',
    icon: ShoppingBag,
    items: [
      { path: '/purchase/indents',     icon: FileText,    label: 'Material Indents' },
      { path: '/purchase/orders',      icon: ShoppingBag, label: 'Purchase Orders' },
      { path: '/purchase/mrn',         icon: Truck,       label: 'MRN' },
      { path: '/purchase/qc',          icon: CheckCircle, label: 'QC Inspection' },
      { path: '/purchase/debit-notes', icon: FileX,       label: 'Debit Notes' },
      { path: '/sales/credit-notes',   icon: FileText,    label: 'Credit Notes' },
    ],
  },
  {
    key: 'production',
    label: 'Production',
    icon: Factory,
    sections: [
      { title: 'Planning',  items: [
        { path: '/production/orders',         icon: ClipboardList,  label: 'Production Orders' },
      ]},
      { title: 'Execution', items: [
        { path: '/production/live-dashboard', icon: Activity,       label: 'Live Dashboard' },
        { path: '/production/entry',          icon: Layers,         label: 'Production Entry' },
        { path: '/production/downtime',       icon: AlertTriangle,  label: 'Downtime Entry' },
      ]},
      { title: 'Masters',   items: [
        { path: '/production/machines',           icon: Cpu,           label: 'Machines' },
        { path: '/production/bom',                icon: Layers,        label: 'BOM' },
        { path: '/production/routing',            icon: Wrench,        label: 'Routing' },
        { path: '/production/cycle-times',        icon: Timer,         label: 'Cycle Times' },
        { path: '/production/downtime-reasons',   icon: AlertTriangle, label: 'Downtime Reasons' },
        { path: '/production/rejection-reasons',  icon: Ban,           label: 'Rejection Reasons' },
      ]},
      { title: 'Reports & Config', items: [
        { path: '/production/daily-summary', icon: BarChart3, label: 'Daily Summary' },
        { path: '/production/supervisor',    icon: Eye,       label: 'Supervisor Dashboard' },
        { path: '/production/dispatch',      icon: Truck,     label: 'Dispatch' },
        { path: '/production/settings',      icon: Settings,  label: 'Settings' },
      ]},
    ],
  },
  {
    key: 'logs',
    label: 'QSA Logs',
    icon: NotebookPen,
    sections: [
      { title: 'Production', items: [
        { path: '/logs/material-issuance',    icon: Package,        label: 'Material Issuance' },
        { path: '/logs/material-movement',    icon: Truck,          label: 'Material Movement' },
        { path: '/logs/material-rejected',    icon: Ban,            label: 'Rejected Material' },
        { path: '/logs/equipment-pouching',   icon: Cpu,            label: 'Equipment – Pouching' },
        { path: '/logs/equipment-printing',   icon: Cpu,            label: 'Equipment – Printing' },
        { path: '/logs/equipment-slitting',   icon: Cpu,            label: 'Equipment – Slitting' },
        { path: '/logs/equipment-doctoring',  icon: Cpu,            label: 'Equipment – Doctoring' },
        { path: '/logs/equipment-lamination', icon: Cpu,            label: 'Equipment – Lamination' },
        { path: '/logs/plates',               icon: Layers,         label: 'Plates Issuance' },
        { path: '/logs/inks',                 icon: Droplet,        label: 'Inks Issuance' },
        { path: '/logs/fg-transfer',          icon: ClipboardCheck, label: 'FG Transfer Note' },
      ]},
      { title: 'QC / QA', items: [
        { path: '/logs/temperature-rh',          icon: Thermometer,   label: 'Temperature & RH' },
        { path: '/logs/sample',                  icon: Beaker,        label: 'Sample Log' },
        { path: '/logs/instrument-usage',        icon: FlaskConical,  label: 'Instrument Usage' },
        { path: '/logs/balance-daily',           icon: Scale,         label: 'Balance – Daily' },
        { path: '/logs/balance-monthly',         icon: Scale,         label: 'Balance – Monthly' },
        { path: '/logs/inspector-qualification', icon: UserCircle,    label: 'Inspector Qualification' },
        { path: '/logs/visual-inspection',       icon: Eye,           label: 'Visual Inspection' },
        { path: '/logs/visual-evaluation',       icon: Eye,           label: 'Inspector Evaluation' },
      ]},
      { title: 'Document Control', items: [
        { path: '/logs/sop-request',         icon: FileText, label: 'SOP Request' },
        { path: '/logs/sop-distribution',    icon: BookOpen, label: 'SOP Distribution' },
        { path: '/logs/artwork-approval',    icon: Image,    label: 'Artwork Approval' },
        { path: '/logs/artwork-destruction', icon: FileX,    label: 'Artwork Destruction' },
      ]},
    ],
  },
  {
    key: 'masters',
    label: 'Masters',
    icon: Boxes,
    items: [
      { path: '/masters/customers',   icon: UserCircle, label: 'Customers' },
      { path: '/masters/vendors',     icon: Building2,  label: 'Vendors' },
      { path: '/masters/departments', icon: Factory,    label: 'Departments' },
      { path: '/masters/shifts',      icon: Clock,      label: 'Shifts' },
      { path: '/masters/plants',      icon: Building2,  label: 'Plants' },
      { path: '/masters/units',       icon: Ruler,      label: 'Unit Master' },
    ],
  },
  {
    key: 'tally',
    label: 'Tally',
    icon: DollarSign,
    items: [
      { path: '/tally/dashboard',         icon: LayoutDashboard, label: 'Tally Dashboard' },
      { path: '/tally/delivery-challan',  icon: Truck,           label: 'Delivery Challan' },
      { path: '/tally/outstanding',       icon: DollarSign,      label: 'Outstanding' },
      { path: '/tally/config',            icon: Shield,          label: 'Tally Config' },
    ],
  },
  {
    key: 'workflow',
    label: 'Workflow',
    icon: GitBranch,
    items: [
      { path: '/workflow',        icon: GitBranch,       label: 'Workflows & Approvals' },
      { path: '/specifications',  icon: FileSpreadsheet, label: 'Specifications & QC' },
      { path: '/task-queue',      icon: ListTodo,        label: 'Task Queue' },
    ],
  },
  {
    key: 'admin',
    label: 'Admin',
    icon: Settings2,
    items: [
      { path: '/admin/users',         icon: Users,     label: 'Users' },
      { path: '/admin/roles',         icon: Shield,    label: 'Roles' },
      { path: '/admin/custom-fields', icon: Settings2, label: 'Custom Fields' },
      { path: '/admin/license',       icon: Key,       label: 'License' },
    ],
  },
];

// Flattens all routes belonging to a group so we can detect the active group from URL.
const groupOf = (pathname) => {
  for (const g of GROUPS) {
    const all = g.sections ? g.sections.flatMap(s => s.items) : (g.items || []);
    if (all.some(it => pathname.startsWith(it.path))) return g.key;
  }
  return GROUPS[0].key;
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initialGroup = useMemo(() => groupOf(location.pathname), [location.pathname]);
  const [activeGroup, setActiveGroup] = useState(initialGroup);
  const [panelOpen, setPanelOpen] = useState(true);
  const [search, setSearch] = useState('');

  // Sync activeGroup when route changes (e.g. browser back/forward)
  useEffect(() => {
    setActiveGroup(groupOf(location.pathname));
  }, [location.pathname]);

  // Clear search when switching modules
  useEffect(() => { setSearch(''); }, [activeGroup]);

  const group = GROUPS.find(g => g.key === activeGroup) || GROUPS[0];
  const sections = group.sections
    ? group.sections
    : [{ title: null, items: group.items }];

  const filterItems = (items) => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it => it.label.toLowerCase().includes(q));
  };

  const visibleSections = sections
    .map(s => ({ ...s, items: filterItems(s.items) }))
    .filter(s => s.items.length > 0);

  const handleRailClick = (key) => {
    setActiveGroup(key);
    setPanelOpen(true);
    // Single-item groups: jump straight to that route
    const g = GROUPS.find(x => x.key === key);
    if (g && !g.sections && g.items.length === 1) {
      navigate(g.items[0].path);
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const mainClass = `main-content${panelOpen ? '' : ' is-rail-only'}`;

  return (
    <div className="App">
      {/* ─── Rail (icon-only column) ─────────────────────────────────────── */}
      <aside className="app-rail" data-testid="app-rail">
        <div className="app-rail-scroll">
          {GROUPS.map((g) => {
            const Icon = g.icon;
            const isActive = activeGroup === g.key;
            return (
              <button
                key={g.key}
                className={`app-rail-btn ${isActive ? 'is-active' : ''}`}
                data-tooltip={g.label}
                data-testid={`rail-${g.key}`}
                onClick={() => handleRailClick(g.key)}
                type="button"
              >
                <span className="icon-wrap">
                  <Icon size={18} strokeWidth={1.8} />
                </span>
                <span>{g.label}</span>
              </button>
            );
          })}
        </div>
        <div className="app-rail-foot">
          <button
            className="app-rail-btn"
            data-tooltip={panelOpen ? 'Collapse panel' : 'Expand panel'}
            onClick={() => setPanelOpen(o => !o)}
            type="button"
            data-testid="toggle-panel-btn"
          >
            <span className="icon-wrap">
              {panelOpen ? <PanelLeftClose size={18} strokeWidth={1.8} /> : <PanelLeftOpen size={18} strokeWidth={1.8} />}
            </span>
            <span>{panelOpen ? 'Hide' : 'Show'}</span>
          </button>
          <button
            className="app-rail-btn"
            data-tooltip="Logout"
            onClick={handleLogout}
            type="button"
            data-testid="rail-logout-btn"
          >
            <span className="icon-wrap"><LogOut size={18} strokeWidth={1.8} /></span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* ─── Secondary panel (items of active group) ─────────────────────── */}
      <aside className={`app-panel ${panelOpen ? '' : 'is-hidden'}`} data-testid="app-panel">
        <div className="app-panel-brand">
          <img src="https://hridya.dweb.co.in/hridayalogo.png" alt="Hridaya" />
        </div>
        <div className="app-panel-header">
          <div className="app-panel-eyebrow">Module</div>
          <h2 className="app-panel-title">{group.label}</h2>
          <div className="app-panel-search">
            <Search size={13} color="#888" />
            <input
              type="text"
              placeholder={`Filter ${group.label.toLowerCase()}…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="panel-search"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, lineHeight: 0 }}
                aria-label="Clear search"
              >
                <X size={13} color="#888" />
              </button>
            )}
          </div>
        </div>

        <div className="app-panel-scroll">
          {visibleSections.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12 }}>
              No matches for “{search}”
            </div>
          ) : (
            visibleSections.map((section, i) => (
              <React.Fragment key={section.title || i}>
                {section.title && <div className="app-panel-subhead">{section.title}</div>}
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) => `app-panel-item ${isActive ? 'active' : ''}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/[\s/]/g, '-')}`}
                    >
                      <Icon size={15} strokeWidth={1.7} />
                      <span style={{ flex: 1 }}>{item.label}</span>
                      <span className="dot" />
                    </NavLink>
                  );
                })}
              </React.Fragment>
            ))
          )}
        </div>
      </aside>

      {/* ─── Main content area ───────────────────────────────────────────── */}
      <div className={mainClass}>
        <header className="top-header" data-testid="top-header">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setPanelOpen(o => !o)}
              className="btn btn-ghost btn-sm"
              data-testid="toggle-sidebar-btn"
              title={panelOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              {panelOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                {group.label}
              </div>
              <h1 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
                Hridaya ERP
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }} data-testid="user-name">{user?.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }} data-testid="user-email">{user?.email}</div>
            </div>
            <div
              style={{
                width: 34, height: 34, borderRadius: 999,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 13, letterSpacing: 0.5,
              }}
              aria-label="User avatar"
            >
              {(user?.name || 'U').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
            </div>
          </div>
        </header>
        <main className="content-area" data-testid="main-content">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
